import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Auth } from "@angular/fire/auth";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
    verifyPasswordResetCode,
    confirmPasswordReset,
    applyActionCode
} from "firebase/auth";

@Component({
    selector: "app-action-handler",
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatProgressSpinnerModule
    ],
    template: `
  <ng-container [ngSwitch]="mode">
    <!-- RESET PASSWORD -->
    <div *ngSwitchCase="'resetPassword'">
      <div class="max-w-md mx-auto mt-12 p-8 bg-white rounded-2xl shadow-lg animate-fade-in space-y-6 relative">
        <div *ngIf="loading" class="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
          <mat-spinner diameter="40"></mat-spinner>
        </div>

        <h2 class="text-3xl font-bold text-center text-gray-700">Restablecer contraseña</h2>

        <div *ngIf="!codeValid && !checked" class="text-center text-sm text-gray-600">Verificando enlace…</div>
        <div *ngIf="!codeValid && checked" class="text-center text-sm text-red-600">El enlace no es válido o expiró.</div>

        <form *ngIf="codeValid" [formGroup]="resetForm" (ngSubmit)="onReset()" class="space-y-4">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Nueva contraseña</mat-label>
            <input
              matInput
              formControlName="password"
              type="password"
              placeholder="••••••••"
            />
            <mat-error *ngIf="resetForm.get('password')?.invalid && resetForm.get('password')?.touched">
              Mínimo 8 caracteres
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Repetí la contraseña</mat-label>
            <input
              matInput
              formControlName="password2"
              type="password"
              placeholder="••••••••"
            />
            <mat-error *ngIf="resetForm.get('password2')?.invalid && resetForm.get('password2')?.touched">
              Las contraseñas deben coincidir
            </mat-error>
          </mat-form-field>

          <button
            mat-raised-button
            color="primary"
            class="w-full py-2 font-semibold tracking-wide"
            type="submit"
            [disabled]="resetForm.invalid || loading"
          >
            {{ loading ? 'Guardando…' : 'Cambiar contraseña' }}
          </button>

          <p *ngIf="success" class="text-center text-sm text-green-700">
            ¡Listo! Tu contraseña fue cambiada.
          </p>
          <p *ngIf="error" class="text-red-600 text-center text-sm mt-2">
            {{ error }}
          </p>

          <div class="mt-6 text-center">
            <a routerLink="/login" class="text-blue-500 hover:text-blue-700">Ir al inicio de sesión</a>
          </div>
        </form>
      </div>
    </div>

    <!-- EMAIL VERIFICATION -->
    <div *ngSwitchCase="'verifyEmail'">
      <div class="max-w-md mx-auto mt-12 p-8 bg-white rounded-2xl shadow-lg animate-fade-in space-y-6 relative">
        <div *ngIf="loading" class="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center">
          <mat-spinner diameter="40"></mat-spinner>
        </div>

        <h2 class="text-3xl font-bold text-center text-gray-700">Verificar email</h2>

        <div *ngIf="loading" class="text-center text-sm text-gray-600">Verificando email…</div>
        <div *ngIf="success" class="text-center text-sm text-green-700">¡Email verificado! Ya puedes iniciar sesión.</div>
        <div *ngIf="error" class="text-red-600 text-center text-sm mt-2">{{ error }}</div>

        <div *ngIf="success" class="mt-6 text-center">
          <a routerLink="/login" class="text-blue-500 hover:text-blue-700">Ir al inicio de sesión</a>
        </div>
      </div>
    </div>

    <div *ngSwitchDefault>
      <div class="max-w-md mx-auto mt-12 p-8 bg-white rounded-2xl shadow-lg animate-fade-in space-y-6">
        <h2 class="text-3xl font-bold text-center text-gray-700">Acción no reconocida</h2>
        <div class="text-center text-sm text-red-600">La acción solicitada no es válida.</div>
        <div class="mt-6 text-center">
          <a routerLink="/login" class="text-blue-500 hover:text-blue-700">Ir al inicio de sesión</a>
        </div>
      </div>
    </div>
  </ng-container>
  `,
    styles: [`
      .animate-fade-in {
        animation: fadeIn 0.5s ease-in-out;
      }

      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `]
})
export class ActionHandlerComponent implements OnInit {
    mode = "";
    oobCode = "";
    loading = false;

    checked = false;
    codeValid = false;

    error = "";
    success = false;

    resetForm: FormGroup;

    constructor(
        private route: ActivatedRoute, 
        private router: Router, 
        private auth: Auth,
        private fb: FormBuilder
    ) {
        this.resetForm = this.fb.group({
            password: ['', [Validators.required, Validators.minLength(8)]],
            password2: ['', [Validators.required]]
        }, { validators: this.passwordMatchValidator });
    }

    passwordMatchValidator(group: FormGroup) {
        const password = group.get('password');
        const password2 = group.get('password2');
        return password && password2 && password.value === password2.value ? null : { passwordMismatch: true };
    }

    async ngOnInit() {
        this.mode = (this.route.snapshot.queryParamMap.get("mode") || "").trim();
        this.oobCode = (this.route.snapshot.queryParamMap.get("oobCode") || "").trim();

        if (!this.oobCode) {
            this.error = "Falta el código de la acción (oobCode).";
            return;
        }

        if (this.mode === "resetPassword") {
            try {
                await verifyPasswordResetCode(this.auth, this.oobCode);
                this.codeValid = true;
            } catch {
                this.codeValid = false;
            } finally {
                this.checked = true;
            }
        }

        if (this.mode === "verifyEmail") {
            this.loading = true;
            try {
                await applyActionCode(this.auth, this.oobCode);
                this.success = true;
            } catch {
                this.error = "El enlace de verificación no es válido o ya fue usado.";
            } finally {
                this.loading = false;
            }
        }
    }

    async onReset() {
        if (this.resetForm.invalid) return;
        
        this.error = ""; 
        this.success = false; 
        this.loading = true;
        
        try {
            const password = this.resetForm.get('password')?.value;
            await confirmPasswordReset(this.auth, this.oobCode, password);
            this.success = true;

            // Opcional: redirigir al login después de unos segundos
            // setTimeout(() => this.router.navigateByUrl('/login'), 1500);
        } catch (e: any) {
            const code = e?.code || "";
            if (code === "auth/weak-password") this.error = "La contraseña es muy débil.";
            else if (code === "auth/expired-action-code") this.error = "El enlace expiró. Pide uno nuevo.";
            else if (code === "auth/invalid-action-code") this.error = "El enlace no es válido.";
            else this.error = "No se pudo cambiar la contraseña. Intenta nuevamente.";
        } finally {
            this.loading = false;
        }
    }
}
